import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { API_BASE, UPLOADS_BASE } from "../constants/api";
import "./D2dCommunicationScreen.css";

const EMPTY_FORM = {
  patient_referral_id: "",
  patient_id: "",
  patient_referral_datetime: "",
  treating_doctor_response_datetime: "",
  referring_doctor_id: "",
  treating_doctor_id: "",
  referring_hospital_id: "",
  referring_doctor_diagnosis_id: "",
  treating_doctor_diagnosis_id: "",
  referring_doctor_procedure_id: "",
  treating_doctor_procedure_id: "",
  reason_for_diagnosis_changes: "",
  reason_for_medical_procedure_changes: "",
  cost_estimation_currency: "USD",
  cost_estimation: "",
  duration_of_hospitalisation: "",
  duration_of_stay_in_india: "",
  recommended_implant_product_ids: [],
  hospital_choice_establishment_ids: [],
  request_a_call: "No",
  scheduled_call_datetime: "",
  treating_doctor_comments: "",
  status: "Pending",
};

const STATUS_OPTIONS = ["Pending", "Responded", "Call Requested", "Closed"];
const CURRENCY_OPTIONS = ["USD", "INR", "EUR"];

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromDateTimeLocal = (value) => (value ? value.replace("T", " ") + ":00" : null);

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const parseIdList = (value) => {
  if (!value || typeof value !== "string") return [];
  return value
    .split(",")
    .map((part) => Number(part.trim()))
    .filter((id) => Number.isFinite(id) && id > 0);
};

const getDoctorEstablishmentIds = (doctor) => {
  if (!doctor) return [];
  const fromCsv = parseIdList(doctor.establishment_ids);
  if (fromCsv.length) return fromCsv;
  if (doctor.establishment_id != null && Number(doctor.establishment_id) > 0) {
    return [Number(doctor.establishment_id)];
  }
  return [];
};

const buildEstablishmentTypeLabel = (item) => {
  const parts = [item.category, item.hospital_type, item.establishment_type]
    .filter(Boolean)
    .map((value) => String(value).trim());
  return parts.length ? parts.join(" / ") : "Healthcare";
};

const buildEstablishmentAddress = (item) => {
  const parts = [
    item.address,
    item.address_line_2,
    item.city_name || item.location_name,
    item.state_name,
    item.country_name,
  ].filter(Boolean);
  return parts.join(", ") || "—";
};

const buildEstablishmentContact = (item) => {
  const phone =
    (Array.isArray(item.phone_numbers) && item.phone_numbers.find((p) => p?.trim())) ||
    item.phone_number_1 ||
    item.phone_number_2 ||
    "";
  if (!phone) return "—";
  const code = item.country_code?.trim();
  if (!code) return phone;
  if (String(phone).startsWith("+") || String(phone).startsWith(code)) return phone;
  return `${code} ${phone}`;
};

const establishmentLogoUrl = (item) => {
  const path = item.logo_path || item.gallery_images?.[0]?.image_path;
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${UPLOADS_BASE}/uploads/${path}`;
};

export default function D2dCommunicationScreen() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const loadData = useCallback(async () => {
    try {
      const endpoints = [
        `${API_BASE}/master/d2d-communications`,
        `${API_BASE}/master/patient-referrals`,
        `${API_BASE}/master/patients`,
        `${API_BASE}/master/doctors`,
        `${API_BASE}/master/establishments`,
        `${API_BASE}/master/case-diagnoses`,
        `${API_BASE}/master/medical-procedures`,
        `${API_BASE}/master/products`,
      ];
      const responses = await Promise.all(endpoints.map((url) => fetch(url)));
      const failed = responses.find((response) => !response.ok);
      if (failed) throw new Error("Unable to load D2D Communication data.");
      const [
        communicationData,
        referralData,
        patientData,
        doctorData,
        establishmentData,
        diagnosisData,
        procedureData,
        productData,
      ] = await Promise.all(responses.map((response) => response.json()));
      setRows(Array.isArray(communicationData) ? communicationData : []);
      setReferrals(Array.isArray(referralData) ? referralData : []);
      setPatients(Array.isArray(patientData) ? patientData : []);
      setDoctors(Array.isArray(doctorData) ? doctorData : []);
      setEstablishments(Array.isArray(establishmentData) ? establishmentData : []);
      setDiagnoses(Array.isArray(diagnosisData) ? diagnosisData : []);
      setProcedures(Array.isArray(procedureData) ? procedureData : []);
      setProducts(Array.isArray(productData) ? productData : []);
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const patientDiagnoses = useMemo(
    () =>
      diagnoses.filter(
        (item) => !form.patient_id || String(item.patient_id) === String(form.patient_id)
      ),
    [diagnoses, form.patient_id]
  );

  const patientReferrals = useMemo(
    () =>
      referrals.filter(
        (item) => !form.patient_id || String(item.patient_id) === String(form.patient_id)
      ),
    [referrals, form.patient_id]
  );

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const applyReferralDefaults = (referralId) => {
    const referral = referrals.find((item) => String(item.referral_id) === String(referralId));
    if (!referral) {
      setField("patient_referral_id", referralId);
      return;
    }
    setForm((prev) => ({
      ...prev,
      patient_referral_id: String(referral.referral_id),
      patient_id: referral.patient_id ? String(referral.patient_id) : prev.patient_id,
      patient_referral_datetime: toDateTimeLocal(
        referral.created_on || referral.referral_date
      ),
      referring_doctor_id: referral.referring_doctor_id
        ? String(referral.referring_doctor_id)
        : "",
      treating_doctor_id: referral.referred_doctor_id_1
        ? String(referral.referred_doctor_id_1)
        : prev.treating_doctor_id,
      referring_hospital_id: referral.referring_establishment_id
        ? String(referral.referring_establishment_id)
        : "",
      referring_doctor_procedure_id: referral.medical_procedure_id || "",
    }));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const handleEdit = (row) => {
    setEditingId(row.communication_id);
    setForm({
      patient_referral_id: row.patient_referral_id ? String(row.patient_referral_id) : "",
      patient_id: row.patient_id ? String(row.patient_id) : "",
      patient_referral_datetime: toDateTimeLocal(row.patient_referral_datetime),
      treating_doctor_response_datetime: toDateTimeLocal(row.treating_doctor_response_datetime),
      referring_doctor_id: row.referring_doctor_id ? String(row.referring_doctor_id) : "",
      treating_doctor_id: row.treating_doctor_id ? String(row.treating_doctor_id) : "",
      referring_hospital_id: row.referring_hospital_id ? String(row.referring_hospital_id) : "",
      referring_doctor_diagnosis_id: row.referring_doctor_diagnosis_id
        ? String(row.referring_doctor_diagnosis_id)
        : "",
      treating_doctor_diagnosis_id: row.treating_doctor_diagnosis_id
        ? String(row.treating_doctor_diagnosis_id)
        : "",
      referring_doctor_procedure_id: row.referring_doctor_procedure_id || "",
      treating_doctor_procedure_id: row.treating_doctor_procedure_id || "",
      reason_for_diagnosis_changes: row.reason_for_diagnosis_changes || "",
      reason_for_medical_procedure_changes:
        row.reason_for_medical_procedure_changes || "",
      cost_estimation_currency: row.cost_estimation_currency || "USD",
      cost_estimation: row.cost_estimation != null ? String(row.cost_estimation) : "",
      duration_of_hospitalisation:
        row.duration_of_hospitalisation != null ? String(row.duration_of_hospitalisation) : "",
      duration_of_stay_in_india:
        row.duration_of_stay_in_india != null ? String(row.duration_of_stay_in_india) : "",
      recommended_implant_product_ids: Array.isArray(row.recommended_implant_product_ids)
        ? row.recommended_implant_product_ids.map(Number)
        : [],
      hospital_choice_establishment_ids: Array.isArray(row.hospital_choice_establishment_ids)
        ? row.hospital_choice_establishment_ids.map(Number)
        : [],
      request_a_call: row.request_a_call || "No",
      scheduled_call_datetime: toDateTimeLocal(row.scheduled_call_datetime),
      treating_doctor_comments: row.treating_doctor_comments || "",
      status: row.status || "Pending",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this D2D communication record?")) return;
    try {
      const response = await fetch(`${API_BASE}/master/d2d-communications/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || "Delete failed.");
      }
      setMessage("Deleted.");
      if (editingId === id) resetForm();
      await loadData();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.patient_referral_id || !form.patient_id) {
      setMessage("Patient Referral ID and Patient ID are required.");
      return;
    }
    setSaving(true);
    setMessage("");
    const payload = {
      patient_referral_id: Number(form.patient_referral_id),
      patient_id: Number(form.patient_id),
      patient_referral_datetime: fromDateTimeLocal(form.patient_referral_datetime),
      treating_doctor_response_datetime: fromDateTimeLocal(form.treating_doctor_response_datetime),
      referring_doctor_id: form.referring_doctor_id ? Number(form.referring_doctor_id) : null,
      treating_doctor_id: form.treating_doctor_id ? Number(form.treating_doctor_id) : null,
      referring_hospital_id: form.referring_hospital_id
        ? Number(form.referring_hospital_id)
        : null,
      referring_doctor_diagnosis_id: form.referring_doctor_diagnosis_id
        ? Number(form.referring_doctor_diagnosis_id)
        : null,
      treating_doctor_diagnosis_id: form.treating_doctor_diagnosis_id
        ? Number(form.treating_doctor_diagnosis_id)
        : null,
      referring_doctor_procedure_id: form.referring_doctor_procedure_id || null,
      treating_doctor_procedure_id: form.treating_doctor_procedure_id || null,
      reason_for_diagnosis_changes: form.reason_for_diagnosis_changes || null,
      reason_for_medical_procedure_changes:
        form.reason_for_medical_procedure_changes || null,
      cost_estimation_currency: form.cost_estimation_currency || null,
      cost_estimation: form.cost_estimation === "" ? null : Number(form.cost_estimation),
      duration_of_hospitalisation:
        form.duration_of_hospitalisation === ""
          ? null
          : Number(form.duration_of_hospitalisation),
      duration_of_stay_in_india:
        form.duration_of_stay_in_india === "" ? null : Number(form.duration_of_stay_in_india),
      recommended_implant_product_ids: form.recommended_implant_product_ids,
      hospital_choice_establishment_ids: form.hospital_choice_establishment_ids,
      request_a_call: form.request_a_call || "No",
      scheduled_call_datetime: fromDateTimeLocal(form.scheduled_call_datetime),
      treating_doctor_comments: form.treating_doctor_comments || null,
      status: form.status || "Pending",
    };

    try {
      const response = await fetch(
        editingId
          ? `${API_BASE}/master/d2d-communications/${editingId}`
          : `${API_BASE}/master/d2d-communications`,
        {
          method: editingId ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || "Save failed.");
      setMessage(editingId ? "Updated." : "Created.");
      resetForm();
      await loadData();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const productNameById = useMemo(() => {
    const map = new Map();
    products.forEach((item) => map.set(Number(item.product_id), item.product_name));
    return map;
  }, [products]);

  const establishmentNameById = useMemo(() => {
    const map = new Map();
    establishments.forEach((item) =>
      map.set(Number(item.establishment_id), item.establishment_name)
    );
    return map;
  }, [establishments]);

  const treatingHospitals = useMemo(() => {
    const treatingDoctor = doctors.find(
      (item) => String(item.doctor_id) === String(form.treating_doctor_id)
    );
    const doctorEstablishmentIds = new Set(getDoctorEstablishmentIds(treatingDoctor));
    const selectedIds = new Set(form.hospital_choice_establishment_ids.map(Number));
    // Only hospitals stored on the treating doctor's establishment_ids.
    const byId = new Map(
      establishments
        .filter((item) => doctorEstablishmentIds.has(Number(item.establishment_id)))
        .map((item) => [Number(item.establishment_id), item])
    );
    selectedIds.forEach((id) => {
      if (!byId.has(id)) {
        const match = establishments.find((item) => Number(item.establishment_id) === id);
        if (match) byId.set(id, match);
      }
    });
    return Array.from(byId.values()).sort((a, b) =>
      String(a.establishment_name || "").localeCompare(String(b.establishment_name || ""))
    );
  }, [
    doctors,
    establishments,
    form.hospital_choice_establishment_ids,
    form.treating_doctor_id,
  ]);

  const toggleHospitalChoice = (establishmentId) => {
    const id = Number(establishmentId);
    setForm((prev) => {
      const selected = prev.hospital_choice_establishment_ids.map(Number).includes(id);
      return {
        ...prev,
        hospital_choice_establishment_ids: selected
          ? prev.hospital_choice_establishment_ids.filter((item) => Number(item) !== id)
          : [...prev.hospital_choice_establishment_ids, id],
      };
    });
  };

  return (
    <div className="d2d-master">
      <Form onSubmit={handleSubmit} className="d2d-form">
        <div className="d2d-section">
          <div className="d2d-section-heading">
            <h5>{editingId ? `Edit Communication #${editingId}` : "Create D2D Communication"}</h5>
            {editingId && (
              <Button type="button" variant="outline-secondary" size="sm" onClick={resetForm}>
                Cancel Edit
              </Button>
            )}
          </div>

          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="d2dPatientId">
              <Form.Label>Patient ID</Form.Label>
              <Form.Select
                required
                value={form.patient_id}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    patient_id: e.target.value,
                    patient_referral_id: "",
                    referring_doctor_diagnosis_id: "",
                    treating_doctor_diagnosis_id: "",
                  }));
                }}
              >
                <option value="">Select patient</option>
                {patients.map((patient) => (
                  <option key={patient.patient_id} value={patient.patient_id}>
                    {patient.patient_id} — {patient.patient_name || "Patient"}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="d2dReferralId">
              <Form.Label>Patient Referral ID</Form.Label>
              <Form.Select
                required
                value={form.patient_referral_id}
                onChange={(e) => applyReferralDefaults(e.target.value)}
                disabled={!form.patient_id}
              >
                <option value="">Select referral</option>
                {patientReferrals.map((referral) => (
                  <option key={referral.referral_id} value={referral.referral_id}>
                    Referral #{referral.referral_id}
                    {referral.referral_date ? ` — ${referral.referral_date}` : ""}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="d2dReferralDt">
              <Form.Label>Patient Referral Datetime</Form.Label>
              <Form.Control
                type="datetime-local"
                value={form.patient_referral_datetime}
                onChange={(e) => setField("patient_referral_datetime", e.target.value)}
              />
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="d2dResponseDt">
              <Form.Label>Treating Doctor Response Datetime</Form.Label>
              <Form.Control
                type="datetime-local"
                value={form.treating_doctor_response_datetime}
                onChange={(e) => setField("treating_doctor_response_datetime", e.target.value)}
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="4" controlId="d2dReferringDoctor">
              <Form.Label>Referring Doctor</Form.Label>
              <Form.Select
                value={form.referring_doctor_id}
                onChange={(e) => setField("referring_doctor_id", e.target.value)}
              >
                <option value="">Select doctor</option>
                {doctors.map((doctor) => (
                  <option key={doctor.doctor_id} value={doctor.doctor_id}>
                    {doctor.doctor_id} — {doctor.doctor_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md="4" controlId="d2dTreatingDoctor">
              <Form.Label>Treating Doctor</Form.Label>
              <Form.Select
                value={form.treating_doctor_id}
                onChange={(e) => setField("treating_doctor_id", e.target.value)}
              >
                <option value="">Select doctor</option>
                {doctors.map((doctor) => (
                  <option key={doctor.doctor_id} value={doctor.doctor_id}>
                    {doctor.doctor_id} — {doctor.doctor_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md="4" controlId="d2dReferringHospital">
              <Form.Label>Referring Hospital</Form.Label>
              <Form.Select
                value={form.referring_hospital_id}
                onChange={(e) => setField("referring_hospital_id", e.target.value)}
              >
                <option value="">Select hospital</option>
                {establishments.map((item) => (
                  <option key={item.establishment_id} value={item.establishment_id}>
                    {item.establishment_id} — {item.establishment_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="d2dRefDiagnosis">
              <Form.Label>Patient Diagnosis (Referring Doctor)</Form.Label>
              <Form.Select
                value={form.referring_doctor_diagnosis_id}
                onChange={(e) => setField("referring_doctor_diagnosis_id", e.target.value)}
                disabled={!form.patient_id}
              >
                <option value="">Select diagnosis</option>
                {patientDiagnoses.map((item) => (
                  <option key={item.case_diagnosis_id} value={item.case_diagnosis_id}>
                    {item.case_diagnosis_id} — {item.diagnosis || item.disease_name || "Diagnosis"}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="d2dTreatDiagnosis">
              <Form.Label>Patient Diagnosis (Treating Doctor)</Form.Label>
              <Form.Select
                value={form.treating_doctor_diagnosis_id}
                onChange={(e) => setField("treating_doctor_diagnosis_id", e.target.value)}
                disabled={!form.patient_id}
              >
                <option value="">Select diagnosis</option>
                {patientDiagnoses.map((item) => (
                  <option key={item.case_diagnosis_id} value={item.case_diagnosis_id}>
                    {item.case_diagnosis_id} — {item.diagnosis || item.disease_name || "Diagnosis"}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="d2dRefProcedure">
              <Form.Label>Recommended Procedure (Referring Doctor)</Form.Label>
              <Form.Select
                value={form.referring_doctor_procedure_id}
                onChange={(e) => setField("referring_doctor_procedure_id", e.target.value)}
              >
                <option value="">Select procedure</option>
                {procedures.map((item) => (
                  <option key={item.procedure_id} value={item.procedure_id}>
                    {item.procedure_id} — {item.procedure_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="d2dTreatProcedure">
              <Form.Label>Recommended Procedure (Treating Doctor)</Form.Label>
              <Form.Select
                value={form.treating_doctor_procedure_id}
                onChange={(e) => setField("treating_doctor_procedure_id", e.target.value)}
              >
                <option value="">Select procedure</option>
                {procedures.map((item) => (
                  <option key={item.procedure_id} value={item.procedure_id}>
                    {item.procedure_id} — {item.procedure_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="d2dReasonDiagnosis">
              <Form.Label>Reason for Diagnosis Changes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.reason_for_diagnosis_changes}
                onChange={(e) => setField("reason_for_diagnosis_changes", e.target.value)}
                placeholder="Enter reason if treating diagnosis differs from referring diagnosis"
              />
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="d2dReasonProcedure">
              <Form.Label>Reason for Medical Procedure Changes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.reason_for_medical_procedure_changes}
                onChange={(e) =>
                  setField("reason_for_medical_procedure_changes", e.target.value)
                }
                placeholder="Enter reason if treating procedure differs from referring procedure"
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="3" controlId="d2dCurrency">
              <Form.Label>Cost Estimation Currency</Form.Label>
              <Form.Select
                value={form.cost_estimation_currency}
                onChange={(e) => setField("cost_estimation_currency", e.target.value)}
              >
                {CURRENCY_OPTIONS.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md="3" controlId="d2dCost">
              <Form.Label>Cost Estimation</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={form.cost_estimation}
                onChange={(e) => setField("cost_estimation", e.target.value)}
              />
            </Form.Group>
            <Form.Group as={Col} md="3" controlId="d2dHospDays">
              <Form.Label>Duration of Hospitalisation</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="1"
                value={form.duration_of_hospitalisation}
                onChange={(e) => setField("duration_of_hospitalisation", e.target.value)}
              />
            </Form.Group>
            <Form.Group as={Col} md="3" controlId="d2dIndiaDays">
              <Form.Label>Duration of Stay in India</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="1"
                value={form.duration_of_stay_in_india}
                onChange={(e) => setField("duration_of_stay_in_india", e.target.value)}
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="12" controlId="d2dImplants">
              <Form.Label>Recommended Implants (Product IDs)</Form.Label>
              <Form.Select
                multiple
                className="d2d-multi-select"
                value={form.recommended_implant_product_ids.map(String)}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions).map((option) =>
                    Number(option.value)
                  );
                  setField("recommended_implant_product_ids", selected);
                }}
              >
                {products.map((item) => (
                  <option key={item.product_id} value={item.product_id}>
                    {item.product_id} — {item.product_name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text muted>Hold Ctrl/Cmd to select multiple products.</Form.Text>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="12" controlId="d2dHospitalChoice">
              <Form.Label>Hospital Choice (Treating Hospitals)</Form.Label>
              <Form.Text muted className="d-block mb-2">
                Select one or more treating hospitals.{" "}
                {form.hospital_choice_establishment_ids.length} selected.
              </Form.Text>
              {treatingHospitals.length === 0 ? (
                <div className="d2d-empty">
                  No hospitals linked to this treating doctor. Assign hospitals in
                  Doctor Master (Establishment IDs).
                </div>
              ) : (
                <div className="d2d-hospital-scroll">
                  {treatingHospitals.map((item) => {
                    const selected = form.hospital_choice_establishment_ids
                      .map(Number)
                      .includes(Number(item.establishment_id));
                    const logoUrl = establishmentLogoUrl(item);
                    return (
                      <button
                        key={item.establishment_id}
                        type="button"
                        className={`d2d-hospital-card${selected ? " is-selected" : ""}`}
                        onClick={() => toggleHospitalChoice(item.establishment_id)}
                      >
                        <div className="d2d-hospital-card__media">
                          {logoUrl ? (
                            <img src={logoUrl} alt="" />
                          ) : (
                            <div className="d2d-hospital-card__placeholder">Hospital</div>
                          )}
                          <span
                            className={`d2d-hospital-card__check${
                              selected ? " is-checked" : ""
                            }`}
                          >
                            {selected ? "✓" : ""}
                          </span>
                        </div>
                        <div className="d2d-hospital-card__body">
                          <div className="d2d-hospital-card__type">
                            {buildEstablishmentTypeLabel(item)}
                          </div>
                          <div className="d2d-hospital-card__name">
                            {item.establishment_name || `Hospital #${item.establishment_id}`}
                          </div>
                          <div className="d2d-hospital-card__detail">
                            Address: {buildEstablishmentAddress(item)}
                          </div>
                          <div className="d2d-hospital-card__detail">
                            Contact: {buildEstablishmentContact(item)}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="3" controlId="d2dRequestCall">
              <Form.Label>Request a Call</Form.Label>
              <Form.Select
                value={form.request_a_call}
                onChange={(e) => setField("request_a_call", e.target.value)}
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md="5" controlId="d2dScheduleCall">
              <Form.Label>Schedule Datetime for a Call</Form.Label>
              <Form.Control
                type="datetime-local"
                value={form.scheduled_call_datetime}
                onChange={(e) => setField("scheduled_call_datetime", e.target.value)}
                disabled={form.request_a_call !== "Yes"}
              />
            </Form.Group>
            <Form.Group as={Col} md="4" controlId="d2dStatus">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="12" controlId="d2dComments">
              <Form.Label>Comments by Treating Doctor</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.treating_doctor_comments}
                onChange={(e) => setField("treating_doctor_comments", e.target.value)}
              />
            </Form.Group>
          </Row>

          <div className="d2d-actions">
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : editingId ? "Update" : "Create"}
            </Button>
            {message && <span className="d2d-message">{message}</span>}
          </div>
        </div>
      </Form>

      <div className="d2d-list">
        <h5>D2D Communication Records</h5>
        {rows.length === 0 ? (
          <p className="text-muted mb-0">No D2D communication records yet.</p>
        ) : (
          <div className="table-responsive">
            <Table hover className="d2d-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Referral</th>
                  <th>Patient</th>
                  <th>Treating Doctor</th>
                  <th>Cost</th>
                  <th>Call</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.communication_id}>
                    <td>{row.communication_id}</td>
                    <td>#{row.patient_referral_id}</td>
                    <td>
                      {row.patient_id}
                      {row.patient_name ? ` — ${row.patient_name}` : ""}
                    </td>
                    <td>{row.treating_doctor_name || row.treating_doctor_id || "—"}</td>
                    <td>
                      {row.cost_estimation != null
                        ? `${row.cost_estimation_currency || ""} ${row.cost_estimation}`.trim()
                        : "—"}
                    </td>
                    <td>
                      {row.request_a_call === "Yes"
                        ? formatDateTime(row.scheduled_call_datetime)
                        : "No"}
                    </td>
                    <td>
                      <span className={`d2d-status d2d-status--${String(row.status || "")
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                      >
                        {row.status || "—"}
                      </span>
                    </td>
                    <td>
                      <div className="d2d-row-actions">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleEdit(row)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(row.communication_id)}
                        >
                          Delete
                        </Button>
                      </div>
                      {(row.hospital_choice_establishment_ids?.length > 0
                        || row.recommended_implant_product_ids?.length > 0
                        || row.reason_for_diagnosis_changes
                        || row.reason_for_medical_procedure_changes) && (
                        <div className="d2d-row-meta">
                          {row.hospital_choice_establishment_ids?.length > 0 && (
                            <div>
                              Hospitals:{" "}
                              {row.hospital_choice_establishment_ids
                                .map(
                                  (id) =>
                                    establishmentNameById.get(Number(id)) || `#${id}`
                                )
                                .join(", ")}
                            </div>
                          )}
                          {row.recommended_implant_product_ids?.length > 0 && (
                            <div>
                              Implants:{" "}
                              {row.recommended_implant_product_ids
                                .map((id) => productNameById.get(Number(id)) || `#${id}`)
                                .join(", ")}
                            </div>
                          )}
                          {row.reason_for_diagnosis_changes ? (
                            <div>
                              Diagnosis change reason: {row.reason_for_diagnosis_changes}
                            </div>
                          ) : null}
                          {row.reason_for_medical_procedure_changes ? (
                            <div>
                              Procedure change reason:{" "}
                              {row.reason_for_medical_procedure_changes}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
